<script setup>
/**
 * @description Tenda official homepage replica using the provided desktop and H5 reference
 * artwork for content sections while reusing the project's BaseHomeBanner component.
 */

import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import { useCardTracking, usePageTracking } from '~/composables/usePageTracking'
import {
  buildCategoryTrackFields,
  buildProductTrackFields,
  isCategoryCard as isTrackCategoryCard,
} from '~/utils/tracking/helpers'
import { rememberProductEntrySource } from '~/utils/tracking/sourceContext'
import {
  hasExposedInPage,
  markExposedInPage,
  useViewportExposure,
} from '~/utils/tracker'
import { BaseMessage } from '~/utils/baseMessage'
import { aggregatePageApi } from '~/api/common/aggregate-page'
import BaseHomeBanner from '~/components/base/HomeBanner.vue'
import { useMediaQuery } from '~/composables/useMediaQuery'
import { useVideoPlayer } from '~/composables/useVideoPlayer'
import { DISPLAY_PAGE } from '@/utils/constant'
import { useUserStore } from '~/store/common/user'
import { resolveAggregateHomeCardMedia } from '@/utils/store/homeAggregate'
import { createPublicContentSsrRequestConfig } from '~/utils/website/ssrRequestPolicy'

// 2. Props
// No props: this route is the official website homepage.

// 3. Emits
// No emits: page-level route.

// 4. Refs/Reactive State
const { withBase } = useBasePath()
const { isMobile } = useMediaQuery()
const userStore = useUserStore()
const isBannerEmpty = ref(false)
const aggregateHomePage = ref(null)
const aggregateLoading = ref(true)
const publicContentSsrEnabled = usePublicContentSsrEnabled()
const { data: ssrAggregateHomePayload } = await useAsyncData(
  'website-home-aggregate-page',
  async () => {
    if (import.meta.client) return null
    if (userStore.isLoggedIn) return null

    const response = await aggregatePageApi.getByKey(
      'HOME',
      { salesOrganization: 'TDC' },
      createPublicContentSsrRequestConfig({ timeout: 3_000 })
    )
    return normalizeAggregatePagePayload(response)
  },
  usePublicContentAsyncDataOptions({ default: () => null })
)

if (publicContentSsrEnabled.value && (import.meta.server || useNuxtApp().isHydrating)) {
  aggregateHomePage.value = ssrAggregateHomePayload.value
  aggregateLoading.value = userStore.isLoggedIn && !aggregateHomePage.value
}
const {
  viewportVideoPlayingStates: productVideoPlayingStates,
  setViewportVideoRef,
  getViewportVideo: getProductVideo,
  playViewportVideo,
  handleViewportVideoPlay,
  handleViewportVideoPause,
  handleViewportVideoEnded,
  handleViewportVideoError,
} = useVideoPlayer({
  onViewportError: ({ key, error, silent }) => {
    if (silent) return
    console.error(`Failed to play home product video for ${key}:`, error)
    BaseMessage.error('视频播放失败')
  },
})

const HOME_SECTION_TYPE = {
  FULL: 0,
  SPLIT: 1,
}

const homeProductSkeletonRows = [
  {
    type: HOME_SECTION_TYPE.FULL,
    count: 1,
  },
  {
    type: HOME_SECTION_TYPE.SPLIT,
    count: 2,
  },
]

// 5. Methods
/**
 * @description 判断链接是否为站外地址
 * @param {string} url - 跳转地址
 * @returns {boolean} 是否为站外地址
 */
const isExternalUrl = (url) => {
  return typeof url === 'string' && /^https?:\/\//.test(url)
}

function resolveAggregateHomeButtonTheme(value) {
  return String(value || '').toUpperCase() === 'BLACK' ? 'black' : 'white'
}

function resolveAggregateHomeTextTheme(product, buttonTheme) {
  if (product.textTheme === 'dark' || product.textTheme === 'light') {
    return product.textTheme
  }

  return buttonTheme === 'white' ? 'dark' : 'light'
}

const aggregateHomeSections = computed(() => {
  const sections = Array.isArray(aggregateHomePage.value?.sections)
    ? aggregateHomePage.value.sections
    : []

  return sections
    .map((section, sectionIndex) => normalizeAggregateHomeSection(section, sectionIndex))
    .filter(Boolean)
})

const resolvedHomeProductSections = computed(() => aggregateHomeSections.value)

function normalizeAggregatePagePayload(payload) {
  if (!payload || typeof payload !== 'object') return null
  if (payload.id || payload.pageKey) return payload
  if (payload.data && typeof payload.data === 'object') {
    return normalizeAggregatePagePayload(payload.data)
  }
  return null
}

function normalizeAggregateHomeSection(section, sectionIndex) {
  if (!section || typeof section !== 'object') return null

  const products = Array.isArray(section.products) ? section.products : []
  const children = products
    .map((product, productIndex) =>
      normalizeAggregateHomeCard(product, section, sectionIndex, productIndex)
    )
    .filter(Boolean)

  if (children.length === 0) return null

  return {
    type: resolveAggregateHomeSectionType(section, children.length),
    children,
  }
}

function normalizeAggregateHomeCard(product, section, sectionIndex, productIndex) {
  if (!product || typeof product !== 'object') return null

  const categoryId = product.categoryId || product.category?.id || ''
  const isCategoryCard = Boolean(categoryId) && !product.prodId
  const customUrl = String(product.pageUrl || '').trim()
  const isCustomCard = Boolean(customUrl) && !isCategoryCard && !product.prodId
  const prodId = isCategoryCard ? '' : product.prodId || product.id
  const media = resolveAggregateHomeCardMedia(product, { mobile: isMobile.value })

  if (!media.url) return null

  const title =
    product.mainTitle ||
    product.modelNo ||
    product.prodName ||
    product.categoryName ||
    product.name ||
    section.name ||
    ''
  const subtitle =
    product.subTitle ||
    product.brief ||
    product.description ||
    product.prodName ||
    product.categoryName ||
    product.name ||
    title
  const detailUrl = isCustomCard
    ? customUrl
    : isCategoryCard
      ? resolveAggregateHomeCategoryListUrl(categoryId)
      : resolveAggregateHomeCardUrl(product, prodId)
  const introUrl = resolveAggregateHomeCardIntroUrl(detailUrl)
  const buttonTheme = resolveAggregateHomeButtonTheme(product.buttonTheme)
  const actions =
    isCategoryCard || isCustomCard
      ? [
          {
            label: product.primaryButtonText || product.buttonText || '了解更多',
            url: detailUrl,
            variant: 'primary',
          },
        ]
      : [
          {
            label: product.primaryButtonText || product.buttonText || '了解更多',
            url: introUrl,
            variant: 'primary',
          },
          {
            label: product.secondaryButtonText || '立即购买',
            url: detailUrl,
            variant: 'outline',
          },
        ]

  return {
    key: `${section.id || section.sectionKey || sectionIndex}-${categoryId || prodId || productIndex}`,
    title,
    subtitle,
    imgUrl: media.imageUrl,
    theme: resolveAggregateHomeTextTheme(product, buttonTheme),
    videoUrl: media.videoUrl,
    buttonTheme,
    actions,
    trackSource: product,
    isCategoryCard,
  }
}

function resolveAggregateHomeSectionType(section, productCount) {
  if (section.layoutType === 'TWO_PER_ROW' || productCount > 1) {
    return HOME_SECTION_TYPE.SPLIT
  }
  return HOME_SECTION_TYPE.FULL
}

function resolveAggregateHomeCardUrl(product, prodId) {
  return (
    (prodId ? `/store/products/${prodId}` : '') ||
    product.detailUrl ||
    product.url ||
    product.linkUrl ||
    product.targetUrl ||
    product.jumpUrl ||
    '/store'
  )
}

function resolveAggregateHomeCategoryListUrl(categoryId) {
  if (!categoryId) return '/store/products'
  return {
    path: '/store/products',
    query: { categoryId: String(categoryId) },
  }
}

function resolveAggregateHomeCardIntroUrl(url) {
  if (typeof url !== 'string') return url
  if (isExternalUrl(url)) return url
  return `${String(url || '/store').replace(/#.*$/, '')}#intro`
}

function navigateHomeLink(url) {
  if (url && typeof url === 'object') {
    navigateTo(url)
    return
  }

  const target = String(url || '').trim()
  if (!target) return
  if (isExternalUrl(target)) {
    navigateTo(target, { external: true, open: { target: '_blank' } })
    return
  }

  navigateTo(target)
}

function getHomeCardPrimaryAction(card) {
  const actions = Array.isArray(card?.actions) ? card.actions : []
  return actions.find((action) => action?.variant === 'primary') || actions[0] || null
}

function handleHomeProductCardClick(card) {
  if (card?.trackSource) {
    const isCategory = isTrackCategoryCard(card.trackSource)
    const fields = isCategory
      ? buildCategoryTrackFields(card.trackSource)
      : buildProductTrackFields(card.trackSource)
    if (!isCategory) {
      rememberProductEntrySource(fields.targetId, {
        sourceType: 'homepage_card',
        sourceId: String(card.key || fields.targetId || 'homepage'),
        sourceName: card.title || fields.targetName || '官网首页',
        fromPage: 'homepage',
      })
    }
    trackHomeCardClick(fields, {
      card_type: isCategory ? 'category' : 'product',
    })
  }
  navigateHomeLink(getHomeCardPrimaryAction(card)?.url)
}

function handleHomeProductCardKeydown(event, card) {
  if (event.key !== 'Enter' && event.key !== ' ') return

  event.preventDefault()
  handleHomeProductCardClick(card)
}

function setProductVideoRef(element, cardKey) {
  if (element) {
    hideProductVideoControls(element)
  }
  setViewportVideoRef(element, cardKey)
}

function hideProductVideoControls(video) {
  if (!video) return
  video.controls = false
  video.removeAttribute('controls')
}

async function playProductVideo(cardKey, { silent = false, reset = true } = {}) {
  hideProductVideoControls(getProductVideo(cardKey))
  return playViewportVideo(cardKey, { silent, reset })
}

function handleProductVideoPlay(cardKey) {
  hideProductVideoControls(getProductVideo(cardKey))
  handleViewportVideoPlay(cardKey)
}

function handleProductVideoPause(cardKey) {
  hideProductVideoControls(getProductVideo(cardKey))
  handleViewportVideoPause(cardKey)
}

function handleProductVideoEnded(cardKey) {
  hideProductVideoControls(getProductVideo(cardKey))
  handleViewportVideoEnded(cardKey)
}

function handleProductVideoError(cardKey) {
  hideProductVideoControls(getProductVideo(cardKey))
  handleViewportVideoError(cardKey)
}

/**
 * @description 在浏览器端加载官网首页活动聚合数据
 * @returns {Promise<void>}
 */
async function loadAggregateHomePage() {
  aggregateLoading.value = true
  try {
    const response = await aggregatePageApi.getByKey('HOME', {
      salesOrganization: 'TDC',
    })
    aggregateHomePage.value = normalizeAggregatePagePayload(response)
  } catch (error) {
    // Keep the public SSR snapshot when the identity-aware client refresh fails.
    console.error('[home] Failed to load aggregate page on client:', error)
  } finally {
    aggregateLoading.value = false
  }
}

usePageTracking({
  pageKey: 'pv_Homepage_page',
  pvEvent: 'pv_Homepage',
  pageName: '官网首页',
  targetType: 'homepage',
  targetId: 'homepage',
  targetName: '官网首页',
})

const { trackCardExposure: trackHomeCardExposure, trackCardClick: trackHomeCardClick } =
  useCardTracking({
    pvEvent: 'pv_HomePage_Card',
    clickEvent: 'pv_HomePage_Card_click',
    pageName: '官网首页',
    targetType: 'homepage_card',
    sourceType: 'homepage_card',
    sourceId: 'homepage',
    sourceName: '官网首页',
  })

const viewportExposure = useViewportExposure({ threshold: 0.5 })

/**
 * @description 初始化首页商品/分类卡片曝光监听
 * @returns {void}
 */
function initHomeCardExposure() {
  viewportExposure.observe(
    '.tenda-home__product-card[data-track-key]:not(.tenda-home__product-card--skeleton)',
    (el) => {
      const cardKey = el?.dataset?.trackKey
      if (!cardKey || hasExposedInPage(`home_card_${cardKey}`)) return

      const allCards = resolvedHomeProductSections.value.flatMap((section) => section.children)
      const card = allCards.find((item) => item.key === cardKey)
      if (!card?.trackSource) return

      markExposedInPage(`home_card_${cardKey}`)
      const fields = isTrackCategoryCard(card.trackSource)
        ? buildCategoryTrackFields(card.trackSource)
        : buildProductTrackFields(card.trackSource)
      trackHomeCardExposure(fields, {
        card_type: isTrackCategoryCard(card.trackSource) ? 'category' : 'product',
      })
    },
    'homepage-card'
  )
}

onMounted(() => {
  loadAggregateHomePage()
  nextTick(() => {
    initHomeCardExposure()
  })
})

watch(resolvedHomeProductSections, () => {
  nextTick(() => viewportExposure.rescan('homepage-card'))
})

onUnmounted(() => {
  viewportExposure.disconnect()
})

definePageMeta({
  layout: false,
})

useSeoMeta({
  title: '腾达(Tenda) 官方网站 - 致力于建设值得信赖的好网络',
  description:
    '腾达（Tenda），全球专业网络通讯设备供应商，集研产销于一体的高新技术企业，主营 WiFi6/7 路由器、交换机、网络摄像机等全系列网络硬件，打造值得信赖的网络解决方案。',
  keywords:
    '腾达，Tenda, 网络通讯设备，WiFi6, WiFi7, 无线路由器，信号放大器，无线网卡，交换机，网络摄像机，接入终端，电力线设备',
})
</script>

<template>
  <div class="tenda-home">
    <BaseHeader />
    <main class="tenda-home__main" aria-labelledby="tenda-home-title">
      <h1 id="tenda-home-title" class="tenda-home__title">Tenda 腾达官网</h1>
      <BaseHomeBanner
        :display-page="DISPLAY_PAGE.HOME"
        :show-visual-overlay="false"
        :tracking="{
          pvEvent: 'pv_HomePage_Banner',
          clickEvent: 'pv_HomePage_Banner_click',
          pageName: '官网首页',
          targetType: 'homepage_banner',
        }"
        @empty="isBannerEmpty = $event"
      />
      <section
        v-if="aggregateLoading"
        class="tenda-home__product-sections"
        :class="{ 'tenda-home__product-sections--banner-empty': isBannerEmpty }"
        aria-label="首页产品推荐加载中"
      >
        <div
          v-for="(row, rowIndex) in homeProductSkeletonRows"
          :key="rowIndex"
          class="tenda-home__product-row"
          :class="`tenda-home__product-row--type-${row.type}`"
        >
          <article
            v-for="card in row.count"
            :key="`${rowIndex}-${card}`"
            class="tenda-home__product-card tenda-home__product-card--skeleton"
            :class="`tenda-home__product-card--type-${row.type}`"
          >
            <a-skeleton :animation="true" class="tenda-home__product-skeleton">
              <div class="tenda-home__product-skeleton-copy">
                <a-skeleton-line :rows="1" :widths="['120px']" :line-height="28" />
                <a-skeleton-line :rows="1" :widths="['320px']" :line-height="36" />
                <div class="tenda-home__product-skeleton-actions">
                  <a-skeleton-shape
                    v-for="item in 2"
                    :key="item"
                    shape="rectangle"
                    class="tenda-home__product-skeleton-button"
                  />
                </div>
              </div>
            </a-skeleton>
          </article>
        </div>
      </section>
      <section
        v-else-if="resolvedHomeProductSections.length > 0"
        class="tenda-home__product-sections"
        :class="{ 'tenda-home__product-sections--banner-empty': isBannerEmpty }"
        aria-label="Tenda 腾达官网首页产品推荐"
      >
        <div
          v-for="(section, sectionIndex) in resolvedHomeProductSections"
          :key="sectionIndex"
          class="tenda-home__product-row"
          :class="`tenda-home__product-row--type-${section.type}`"
        >
          <article
            v-for="card in section.children"
            :key="card.key"
            class="tenda-home__product-card"
            :data-track-key="card.key"
            :class="[
              `tenda-home__product-card--theme-${card.theme}`,
              `tenda-home__product-card--button-theme-${card.buttonTheme}`,
              `tenda-home__product-card--type-${section.type}`,
              {
                'tenda-home__product-card--has-video': card.videoUrl,
                'tenda-home__product-card--clickable': getHomeCardPrimaryAction(card)?.url,
              },
            ]"
            :style="card.imgUrl ? { backgroundImage: `url(${withBase(card.imgUrl)})` } : null"
            :role="getHomeCardPrimaryAction(card)?.url ? 'link' : undefined"
            :tabindex="getHomeCardPrimaryAction(card)?.url ? 0 : undefined"
            @click="handleHomeProductCardClick(card)"
            @keydown="handleHomeProductCardKeydown($event, card)"
          >
            <video
              v-if="card.videoUrl"
              :ref="(el) => setProductVideoRef(el, card.key)"
              class="tenda-home__product-video"
              :poster="card.imgUrl ? withBase(card.imgUrl) : undefined"
              :src="withBase(card.videoUrl)"
              muted
              playsinline
              webkit-playsinline
              x5-video-player-type="h5"
              preload="metadata"
              :controls="false"
              @ended="handleProductVideoEnded(card.key)"
              @error="handleProductVideoError(card.key)"
              @pause="handleProductVideoPause(card.key)"
              @play="handleProductVideoPlay(card.key)"
            ></video>
            <button
              v-if="card.videoUrl && !productVideoPlayingStates[card.key]"
              type="button"
              class="tenda-home__product-play"
              aria-label="播放视频"
              @click.stop="playProductVideo(card.key)"
              @keydown.stop
            ></button>
            <div class="tenda-home__product-copy">
              <p class="tenda-home__product-title">{{ card.title }}</p>
              <h2 class="tenda-home__product-subtitle">{{ card.subtitle }}</h2>
              <div class="tenda-home__product-actions" @click.stop @keydown.stop>
                <template v-for="action in card.actions" :key="`${card.key}-${action.label}`">
                  <a
                    v-if="isExternalUrl(action.url)"
                    :href="action.url"
                    :class="[
                      'tenda-home__product-action',
                      `tenda-home__product-action--${action.variant}`,
                    ]"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ action.label }}
                  </a>
                  <NuxtLink
                    v-else
                    :to="action.url"
                    :class="[
                      'tenda-home__product-action',
                      `tenda-home__product-action--${action.variant}`,
                    ]"
                  >
                    {{ action.label }}
                  </NuxtLink>
                </template>
              </div>
            </div>
          </article>
        </div>
      </section>
    </main>

    <!-- 客服机器人组件：仅客户端挂载，SSR 不渲染 -->
    <ClientOnly>
      <BaseChatbot />
    </ClientOnly>

    <BaseFooter />
  </div>
</template>

<style lang="scss" scoped>
@use '~/styles/storeHome.scss';

.tenda-home {
  width: 100%;
  min-height: 100vh;
  background-color: $bg-white;
  overflow-x: hidden;

  &__main {
    padding-top: $base-header-height;
  }

  &__title {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  &__product-sections {
    width: 100%;
    max-width: none;
    margin: 6px 0 0;
    background-color: $bg-white;

    &--banner-empty {
      margin-top: 0;
    }
  }

  &__product-row {
    display: grid;
    gap: 6px;

    & + & {
      margin-top: 6px;
    }

    &--type-0 {
      grid-template-columns: 1fr;
    }

    &--type-1 {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }

  &__product-card {
    position: relative;
    display: block;
    width: 100%;
    overflow: hidden;
    background-repeat: no-repeat;
    background-position: center;
    background-size: cover;

    &--type-0 {
      aspect-ratio: 2560 / 1013;
    }

    &--type-1 {
      aspect-ratio: 1250 / 786;
    }

    &--theme-dark {
      color: $bg-white;
    }

    &--theme-light {
      color: $color-black;
    }

    &--skeleton {
      background: linear-gradient(180deg, #f7f8fa 0%, #eef1f5 100%);
    }

    &--has-video {
      background-color: $color-black;
    }

    &--clickable {
      cursor: pointer;
    }

    &--button-theme-white {
      .tenda-home__product-action--primary {
        color: $color-black;
        background-color: $bg-white;
        border-color: $bg-white;
      }

      .tenda-home__product-action--outline {
        color: $bg-white;
        border-color: $bg-white;
      }
    }

    &--button-theme-black {
      .tenda-home__product-action--primary {
        color: $bg-white;
        background-color: $color-black;
        border-color: $color-black;
      }

      .tenda-home__product-action--outline {
        color: $color-black;
        border-color: $color-black;
      }
    }
  }

  &__product-video {
    position: absolute;
    inset: 0;
    z-index: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    pointer-events: none;
  }

  &__product-play {
    position: absolute;
    top: 32px;
    right: 32px;
    z-index: 3;
    @include storeHome.home-video-play-button;
  }

  &__product-copy {
    position: absolute;
    width: calc(100% - 32px);
    top: clamp(16px, calc(6.3333vw - 69.6px), 52px);
    left: 50%;
    z-index: 2;
    text-align: center;
    transform: translateX(-50%);
    line-height: 1;
  }

  &__product-title {
    margin: 0;
    font-size: clamp(14px, 1.0417vw, 20px);
    line-height: clamp(22px, 1.4583vw, 28px);
  }

  &__product-subtitle {
    margin: clamp(6px, calc(1vw - 7.2px), 12px) 0 0;
    font-size: clamp(20px, 1.25vw, 24px);
    line-height: clamp(28px, 1.875vw, 36px);
    font-weight: 700;
  }

  &__product-actions {
    display: flex;
    justify-content: center;
    gap: clamp(12px, 1.0417vw, 20px);
    margin-top: clamp(10px, calc(4.4792vw - 54px), 32px);
  }

  &__product-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: clamp(72px, 4.5833vw, 88px);
    height: clamp(32px, 1.875vw, 36px);
    padding: 0 clamp(14px, 0.9375vw, 18px);
    border: 2px solid currentColor;
    border-radius: clamp(4px, 0.3125vw, 6px);
    font-size: clamp(12px, 0.7292vw, 14px);
    line-height: 1;
    font-weight: $font-weight-medium;
    text-decoration: none;
    transition:
      color 0.2s ease,
      background-color 0.2s ease,
      border-color 0.2s ease;

    &--primary {
      color: $bg-white;
      background-color: $color-black;
      border-color: $color-black;
    }

    &--outline {
      color: inherit;
      background-color: transparent;
      border-color: currentColor;
    }

    &:hover {
      color: $primary-color;
      border-color: $primary-color;
    }

    &--primary:hover {
      color: $bg-white;
      background-color: $primary-color;
      border-color: $primary-color;
    }
  }

  &__product-skeleton {
    display: block;
    width: 100%;
    height: 100%;

    :deep(.arco-skeleton) {
      width: 100%;
      height: 100%;
    }
  }

  &__product-skeleton-copy {
    position: absolute;
    top: clamp(16px, calc(6.3333vw - 69.6px), 52px);
    left: 50%;
    z-index: 2;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: clamp(8px, 0.625vw, 12px);
    width: calc(100% - 32px);
    transform: translateX(-50%);
  }

  &__product-skeleton-actions {
    display: flex;
    gap: clamp(12px, 1.0417vw, 20px);
    margin-top: clamp(10px, calc(1.9792vw - 18px), 20px);
  }

  &__product-skeleton-button {
    width: clamp(72px, 4.5833vw, 88px);
    height: clamp(32px, 1.875vw, 36px);
    border-radius: clamp(4px, 0.3125vw, 6px);
  }
}

@include respond-to('xxxl') {
  .tenda-home {
    &__product-copy,
    &__product-skeleton-copy {
      top: 52px;
    }

    &__product-subtitle {
      margin-top: 12px;
    }

    &__product-actions {
      margin-top: 32px;
    }

    &__product-skeleton-actions {
      margin-top: 20px;
    }
  }
}

@include respond-to-max('lg') {
  .tenda-home {
    &__main {
      padding-top: $base-header-height-mobile;
    }

    &__product-sections {
      max-width: none;
    }

    &__product-row,
    &__product-row--type-1 {
      grid-template-columns: 1fr;
    }

    &__product-card,
    &__product-card--type-0,
    &__product-card--type-1 {
      aspect-ratio: 1200 / 1600;
    }

    &__product-copy,
    &__product-row--type-1 &__product-copy {
      width: calc(100% - 24px);
      top: 44px;
    }

    &__product-title {
      font-size: $font-size-body-1;
      line-height: $line-height-body-1;
    }

    &__product-subtitle {
      margin-top: 8px;
      font-size: 20px;
      line-height: 28px;
    }

    &__product-actions {
      gap: 16px;
      margin-top: 12px;
    }

    &__product-action {
      min-width: 88px;
      height: 32px;
      padding: 0 14px;
      border-radius: 6px;
      font-size: $font-size-body-1;
    }

    &__product-play {
      top: 16px;
      right: 16px;
    }

    &__product-skeleton-copy {
      width: calc(100% - 24px);
      top: 44px;
      gap: 8px;
    }

    &__product-skeleton-actions {
      gap: 16px;
      margin-top: 4px;
    }

    &__product-skeleton-button {
      height: 32px;
      width: 80px;
      border-radius: 6px;
    }
  }
}
</style>
